/**
 * Edit Company/Contact Form Component
 *
 * Form component for editing companies and contacts with pre-filled data.
 */

import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useCompanyCategories, useUpdateCompany } from '../hooks';
import type { Company, CompanyGroupName, ContactType, UpdateCompanyInput } from '../types';

interface Props {
  company: Company;
  contactType: ContactType;
  onSuccess?: (company: any) => void;
  onCancel?: () => void;
  isPending?: boolean;
}

const GROUP_OPTIONS: CompanyGroupName[] = ['Closing', 'Payoffs', 'Services', 'Government', 'Other'];

// Helper component for optional label
function OptionalLabel({ children }: { children: React.ReactNode }) {
  return <span className="text-muted-foreground font-normal"> {children}</span>;
}

export function EditCompanyForm({
  company,
  contactType,
  onSuccess,
  onCancel,
  isPending: externalPending,
}: Props) {
  const [formData, setFormData] = useState<UpdateCompanyInput>({
    category_id: company.category_id || '',
    name: company.name || '',
    email: company.email || '',
    phone: company.phone || '',
    fax: company.fax || '',
    website: company.website || '',
    primary_contact_name: company.primary_contact_name || '',
    primary_contact_title: company.primary_contact_title || '',
    primary_contact_email: company.primary_contact_email || '',
    address_street: company.address_street || '',
    address_unit: company.address_unit || '',
    address_city: company.address_city || '',
    address_state: company.address_state || '',
    address_zip: company.address_zip || '',
    address_country: company.address_country || 'USA',
    tags: company.tags || [],
    internal_notes: company.internal_notes || '',
    license_number: company.license_number || '',
    license_state: company.license_state || '',
    license_expiry: company.license_expiry || '',
    nmls_id: company.nmls_id || '',
  });

  const [selectedGroup, setSelectedGroup] = useState<CompanyGroupName | ''>(
    company.category?.group_name || '',
  );
  const [tagsInput, setTagsInput] = useState(company.tags?.join(', ') || '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: categories } = useCompanyCategories();
  const updateCompany = useUpdateCompany();

  const isPending = externalPending || updateCompany.isPending;

  // Filter categories by selected group
  const filteredCategories = selectedGroup
    ? categories?.filter((c) => c.group_name === selectedGroup)
    : categories;

  // Check if form is valid
  const isFormValid = () => {
    if (!formData.name?.trim() && !formData.primary_contact_name?.trim()) {
      return false;
    }
    if (formData.name?.trim() && !formData.category_id) {
      return false;
    }
    return true;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // For individual contacts, primary contact name is required
    if (contactType === 'individual') {
      if (!formData.primary_contact_name?.trim()) {
        newErrors.general = 'Primary contact name is required for individual contacts';
      }
    } else {
      // For organizations, either company name or primary contact name is required
      if (!formData.name?.trim() && !formData.primary_contact_name?.trim()) {
        newErrors.general = 'Either company name or primary contact name is required';
      }

      // Category is required when company name is provided
      if (formData.name?.trim() && !formData.category_id) {
        newErrors.category_id = 'Category is required when company name is provided';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    const tags = tagsInput
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);

    const data: UpdateCompanyInput = {
      ...formData,
      tags,
      category_id: formData.name?.trim() ? formData.category_id : undefined,
    };

    // Remove empty string values
    Object.keys(data).forEach((key) => {
      if (
        typeof data[key as keyof UpdateCompanyInput] === 'string' &&
        !(data[key as keyof UpdateCompanyInput] as string)
      ) {
        delete data[key as keyof UpdateCompanyInput];
      }
    });

    try {
      const result = await updateCompany.mutateAsync({
        id: company.id,
        data,
      });

      onSuccess?.(result);
    } catch (err) {
      console.error('Failed to update company:', err);
    }
  };

  const handleInputChange = (field: keyof UpdateCompanyInput, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field] || errors.general) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors.general;
        return newErrors;
      });
    }
  };

  const handleGroupChange = (group: CompanyGroupName) => {
    setSelectedGroup(group);
    setFormData((prev) => ({ ...prev, category_id: '' }));
  };

  const hasCompanyName = !!formData.name?.trim();
  const categoryRequired = hasCompanyName && contactType === 'organization';
  const primaryContactRequired = !hasCompanyName && contactType === 'organization';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
          <p className="text-sm text-destructive">{errors.general}</p>
        </div>
      )}

      {/* Company Details - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">Company Details</h3>
          </div>
          <div className="p-5 space-y-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <Label htmlFor="name" className="text-sm font-medium">
                  Company Name
                  {!primaryContactRequired && <OptionalLabel>(optional)</OptionalLabel>}
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  placeholder="e.g., ABC Title Company"
                  className={errors.name ? 'border-destructive' : 'mt-2'}
                />
                {!hasCompanyName && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Provide a company name or add a primary contact below
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="group" className="text-sm font-medium">
                  Group{categoryRequired && <span className="text-destructive">*</span>}
                </Label>
                <Select
                  value={selectedGroup}
                  onValueChange={handleGroupChange}
                  disabled={!formData.name?.trim()}
                >
                  <SelectTrigger className="mt-2">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent>
                    {GROUP_OPTIONS.map((group) => (
                      <SelectItem key={group} value={group}>
                        {group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="category_id" className="text-sm font-medium">
                  Category{categoryRequired && <span className="text-destructive">*</span>}
                </Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => handleInputChange('category_id', value)}
                  disabled={!selectedGroup || !formData.name?.trim()}
                >
                  <SelectTrigger
                    className={`mt-2 ${errors.category_id ? 'border-destructive' : ''}`}
                  >
                    <SelectValue
                      placeholder={
                        !formData.name?.trim()
                          ? 'Enter company name first'
                          : selectedGroup
                            ? 'Select category'
                            : 'Select group first'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCategories?.map((category) => (
                      <SelectItem key={category.id} value={category.id}>
                        {category.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.category_id && (
                  <p className="text-xs text-destructive mt-2">{errors.category_id}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Contact - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">Company Contact</h3>
          </div>
          <div className="p-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="email" className="text-sm font-medium">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="company@example.com"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="phone" className="text-sm font-medium">
                  Phone
                </Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="fax" className="text-sm font-medium">
                  Fax
                </Label>
                <Input
                  id="fax"
                  value={formData.fax}
                  onChange={(e) => handleInputChange('fax', e.target.value)}
                  placeholder="(555) 123-4568"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="website" className="text-sm font-medium">
                  Website
                </Label>
                <Input
                  id="website"
                  value={formData.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                  placeholder="www.example.com"
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary Contact - Always show, but make required for individual contacts */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
          <h3 className="text-sm font-medium text-foreground">
            Primary Contact
            {primaryContactRequired && <span className="text-destructive ml-1">*</span>}
          </h3>
        </div>
        <div className="p-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div>
              <Label htmlFor="primary_contact_name" className="text-sm font-medium">
                Name{primaryContactRequired && <span className="text-destructive">*</span>}
              </Label>
              <Input
                id="primary_contact_name"
                value={formData.primary_contact_name}
                onChange={(e) => handleInputChange('primary_contact_name', e.target.value)}
                placeholder="e.g., John Smith"
                className={`mt-2 ${errors.primary_contact_name ? 'border-destructive' : ''}`}
              />
              {!primaryContactRequired && (
                <p className="text-xs text-muted-foreground mt-2">
                  Optional when company name is provided
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="primary_contact_title" className="text-sm font-medium">
                Title
              </Label>
              <Input
                id="primary_contact_title"
                value={formData.primary_contact_title}
                onChange={(e) => handleInputChange('primary_contact_title', e.target.value)}
                placeholder="e.g., Account Manager"
                className="mt-2"
              />
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="primary_contact_email" className="text-sm font-medium">
                Email
              </Label>
              <Input
                id="primary_contact_email"
                type="email"
                value={formData.primary_contact_email}
                onChange={(e) => handleInputChange('primary_contact_email', e.target.value)}
                placeholder="john.smith@example.com"
                className="mt-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Address */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
          <h3 className="text-sm font-medium text-foreground">Address</h3>
        </div>
        <div className="p-5">
          <div className="grid gap-5 md:grid-cols-2">
            <div className="md:col-span-2">
              <Label htmlFor="address_street" className="text-sm font-medium">
                Street Address
              </Label>
              <Input
                id="address_street"
                value={formData.address_street}
                onChange={(e) => handleInputChange('address_street', e.target.value)}
                placeholder="123 Main Street"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_unit" className="text-sm font-medium">
                Unit/Suite
              </Label>
              <Input
                id="address_unit"
                value={formData.address_unit}
                onChange={(e) => handleInputChange('address_unit', e.target.value)}
                placeholder="Apt 4B"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_city" className="text-sm font-medium">
                City
              </Label>
              <Input
                id="address_city"
                value={formData.address_city}
                onChange={(e) => handleInputChange('address_city', e.target.value)}
                placeholder="Denver"
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_state" className="text-sm font-medium">
                State
              </Label>
              <Input
                id="address_state"
                value={formData.address_state}
                onChange={(e) => handleInputChange('address_state', e.target.value)}
                placeholder="CO"
                maxLength={2}
                className="mt-2"
              />
            </div>

            <div>
              <Label htmlFor="address_zip" className="text-sm font-medium">
                ZIP Code
              </Label>
              <Input
                id="address_zip"
                value={formData.address_zip}
                onChange={(e) => handleInputChange('address_zip', e.target.value)}
                placeholder="80202"
                className="mt-2"
              />
            </div>
          </div>
        </div>
      </div>

      {/* License Information - Hide for individual contacts */}
      {contactType === 'organization' && (
        <div className="border border-border/40 rounded-lg overflow-hidden">
          <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
            <h3 className="text-sm font-medium text-foreground">License Information</h3>
          </div>
          <div className="p-5">
            <div className="grid gap-5 md:grid-cols-2">
              <div>
                <Label htmlFor="license_number" className="text-sm font-medium">
                  License Number
                </Label>
                <Input
                  id="license_number"
                  value={formData.license_number}
                  onChange={(e) => handleInputChange('license_number', e.target.value)}
                  placeholder="e.g., 123456"
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="license_state" className="text-sm font-medium">
                  License State
                </Label>
                <Input
                  id="license_state"
                  value={formData.license_state}
                  onChange={(e) => handleInputChange('license_state', e.target.value)}
                  placeholder="CO"
                  maxLength={2}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="license_expiry" className="text-sm font-medium">
                  License Expiry
                </Label>
                <Input
                  id="license_expiry"
                  type="date"
                  value={formData.license_expiry}
                  onChange={(e) => handleInputChange('license_expiry', e.target.value)}
                  className="mt-2"
                />
              </div>

              <div>
                <Label htmlFor="nmls_id" className="text-sm font-medium">
                  NMLS ID
                </Label>
                <Input
                  id="nmls_id"
                  value={formData.nmls_id}
                  onChange={(e) => handleInputChange('nmls_id', e.target.value)}
                  placeholder="e.g., 123456"
                  className="mt-2"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Additional Information */}
      <div className="border border-border/40 rounded-lg overflow-hidden">
        <div className="px-5 py-3 border-b border-border/40 bg-muted/20">
          <h3 className="text-sm font-medium text-foreground">Additional Information</h3>
        </div>
        <div className="p-5 space-y-5">
          <div>
            <Label htmlFor="tags" className="text-sm font-medium">
              Tags <OptionalLabel>(optional)</OptionalLabel>
            </Label>
            <Input
              id="tags"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              placeholder="e.g., preferred, local, fast-closing"
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">Separate multiple tags with commas</p>
          </div>

          <div>
            <Label htmlFor="internal_notes" className="text-sm font-medium">
              Internal Notes <OptionalLabel>(optional)</OptionalLabel>
            </Label>
            <Textarea
              id="internal_notes"
              value={formData.internal_notes}
              onChange={(e) => handleInputChange('internal_notes', e.target.value)}
              placeholder="Private notes about this company or contact..."
              rows={3}
              className="mt-2 resize-none"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pt-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={isPending || !isFormValid()} className="min-w-[160px]">
          {isPending ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save Changes'
          )}
        </Button>
      </div>

      {/* Help text */}
      <div className="border border-border/40 rounded-lg bg-muted/20 p-4">
        <p className="text-sm font-medium text-foreground mb-2">Required fields:</p>
        <ul className="space-y-1 text-sm text-muted-foreground">
          {contactType === 'individual' ? (
            <li className="flex items-start gap-2">
              <span className="text-muted-foreground mt-0.5">•</span>
              <span>
                <strong>Primary Contact Name</strong> (required for individual contacts)
              </span>
            </li>
          ) : (
            <>
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground mt-0.5">•</span>
                <span>
                  Either <strong>Company Name</strong> OR <strong>Primary Contact Name</strong> (or
                  both)
                </span>
              </li>
              {hasCompanyName && (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>
                      <strong>Group</strong> (required when company name is provided)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-muted-foreground mt-0.5">•</span>
                    <span>
                      <strong>Category</strong> (required when company name is provided)
                    </span>
                  </li>
                </>
              )}
              {!hasCompanyName && (
                <li className="flex items-start gap-2">
                  <span className="text-muted-foreground mt-0.5">•</span>
                  <span>
                    <strong>Primary Contact Name</strong> (required for contact-only entries)
                  </span>
                </li>
              )}
            </>
          )}
        </ul>
      </div>
    </form>
  );
}

EditCompanyForm.displayName = 'EditCompanyForm';
